/*
 * Copyright Mimic Networks, Inc. 2025.
 */

/* eslint-disable react/prop-types */
import { Collapse } from 'antd';
import React, { useMemo } from 'react';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Text } from '@/primitives/Text';

import { createActorGroupItem } from './ActorGroup';
import { getActorCount, groupRulesByActor } from './utils/aclGrouping';
import { rulesTextStyle } from './utils/styles';
import { ACLRule } from './utils/types';

interface ACLRulesListProps {
  rules: ACLRule[];
}

export const ACLRulesList = React.memo<ACLRulesListProps>(({ rules }) => {
  const { t } = useMimicTranslation('configPrettyViewer');

  const { actorGroups, actorCount } = useMemo(() => {
    const groups = groupRulesByActor(rules);
    return {
      actorGroups: groups,
      actorCount: getActorCount(rules),
    };
  }, [rules]);

  const collapseItems = useMemo(() => {
    return Object.entries(actorGroups).map(([actor, group]) =>
      createActorGroupItem({ actor, actorType: group.type, rules: group.rules }, t),
    );
  }, [actorGroups, t]);

  if (!rules.length) {
    return null;
  }

  return (
    <div>
      <Text style={rulesTextStyle}>
        {t('aclProtection.rulesGrouped', {
          count: rules.length,
          actorCount,
        })}
      </Text>
      <Collapse ghost size="small" style={{ backgroundColor: 'transparent' }} items={collapseItems} />
    </div>
  );
});
